<template>
    <div
        v-if="showBanner"
        id="cookie-banner"
        class="fixed md:absolute bottom-0 inset-x-0 md:left-auto md:right-0 md:mb-10 md:mr-10 bg-white shadow-[0px_-4px_12px_0px_rgba(0,0,0,0.24)] md:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.24)] py-10 px-6 flex flex-col gap-6 items-center justify-between md:max-w-sm z-30 rounded-t-3xl md:rounded-3xl">
        <h3 class="title-subsection">Cookie preferences</h3>
        <p class="details md:body-small text-valence-grey-750">
            We use cookies to provide a better experience. Choose "Only Necessary" to allow only the cookies required for Nadia to function, or "Accept All Cookies" to also enable
            performance and analytics cookies that help us improve the app.
            <a class="font-semibold underline text-valence-primary-500" href="https://www.valence.co/other/privacy#cookies" target="_blank">Learn more here</a>.
        </p>
        <div class="flex flex-col items-center justify-between gap-4">
            <button
                class="max-h-12 py-3 px-5 leading-none rounded-3xl h-auto w-full font-semibold bg-valence-primary-500 text-white"
                data-testid="accept-cookie-button"
                @click="acceptAllCookies">
                Accept All Cookies
            </button>
            <button
                class="max-h-12 py-3 px-5 leading-none rounded-3xl h-auto w-full font-semibold text-valence-primary-500"
                data-testid="reject-cookie-button"
                @click="rejectCookies">
                Only Necessary
            </button>
        </div>
    </div>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { usePage } from "@inertiajs/vue3";
import { onMounted, ref } from "vue";

const showBanner = ref(false);

onMounted(async () => {
    // Native apps don't need cookie consent banners — privacy is handled
    // via App Store privacy labels and iOS App Tracking Transparency.
    if (usePage().props?.is_mobile_app) {
        return;
    }
    const cookieConsent = localStorage.getItem("cookie_consent");
    if (cookieConsent === "persisted") {
        return;
    }

    const response = await fetch("/accounts/cookie-preferences/" + (cookieConsent || ""), {
        method: "GET",
        headers: { "X-CSRFToken": getCookie("csrftoken") },
    });
    if (response.status != 200) {
        console.error("Failed to get cookie preferences");
        return;
    }
    const data = await response.json();
    if (!data.cookie_preferences) {
        showBanner.value = true;
        return;
    }

    if (data.cookie_preferences === "updated" || !cookieConsent) {
        localStorage.setItem("cookie_consent", "persisted");
    }
});

const updatePreferences = async (choice) => {
    const response = await fetch("/accounts/cookie-preferences/", {
        method: "PUT",
        body: JSON.stringify({ choice, method: "banner" }),
        headers: { "X-CSRFToken": getCookie("csrftoken"), "Content-Type": "application/json" },
    });
    if (response.status != 204) {
        console.error("Failed to update cookie preferences");
        return null;
    }
    localStorage.setItem("cookie_consent", `?consent_method=banner&consent_choice=${choice}&consent_at=${new Date().toISOString()}`);
    return response;
};

async function rejectCookies() {
    const response = await updatePreferences("performance");
    if (response) {
        showBanner.value = false;
    }
}

async function acceptAllCookies() {
    const response = await updatePreferences("all");
    if (response) {
        showBanner.value = false;
    }
}
</script>
